/**
 * Web Location Service Implementation
 * Implements ILocationService using web platform services and existing RestaurantService
 */

import type { ILocationService, UserLocation, NearestLocationResult } from '../../contracts'
import type { Location, FormatHoursOptions } from '@restaurant/core'
import { formatLocationHoursText, isLocationAcceptingOrders } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'


export class WebLocationService implements ILocationService {
  private restaurantService: any
  private storageKey = 'selectedLocationId'
  private _isDebugMode: boolean

  constructor(restaurantService: any, isDebugMode = false) {
    this.restaurantService = restaurantService
    this._isDebugMode = isDebugMode
  }

  async getLocations(restaurantId: string): Promise<Location[]> {
    try {
      return await this.restaurantService.getLocations(restaurantId)
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to load locations', { error: String(error) })
      throw new Error('Failed to load locations')
    }
  }

  async getNearestLocation(coordinates: UserLocation): Promise<NearestLocationResult> {
    try {
      return await this.restaurantService.getNearestLocation(coordinates)
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to find nearest location', { error: String(error) })
      throw new Error('Failed to find nearest location')
    }
  }

  async searchLocations(locations: Location[], query: string): Promise<Location[]> {
    const searchTerm = query.toLowerCase().trim()

    if (!searchTerm) {
      return locations
    }

    return locations.filter(location =>
      location.name.toLowerCase().includes(searchTerm) ||
      location.address.toLowerCase().includes(searchTerm) ||
      location.city.toLowerCase().includes(searchTerm) ||
      location.zipCode.includes(searchTerm)
    )
  }

  async getLocationDetails(restaurantId: string, locationId: string): Promise<Location> {
    try {
      return await this.restaurantService.getLocation(restaurantId, locationId)
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to load location details', { error: String(error) })
      throw new Error('Failed to load location details')
    }
  }

  calculateDistance(userLocation: UserLocation, location: Location): string {
    const R = 3959 // Earth's radius in miles
    const dLat = this.deg2rad(location.latitude - userLocation.latitude)
    const dLon = this.deg2rad(location.longitude - userLocation.longitude)

    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.deg2rad(userLocation.latitude)) * Math.cos(this.deg2rad(location.latitude)) *
      Math.sin(dLon/2) * Math.sin(dLon/2)

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    const distance = R * c // Distance in miles

    if (distance < 0.1) {
      return '(< 0.1 miles away)'
    } else if (distance < 1) {
      return `(${distance.toFixed(1)} miles away)`
    } else {
      return `(${distance.toFixed(1)} miles away)`
    }
  }

  formatHoursText(location: Location, options?: FormatHoursOptions): string {
    if (this._isDebugMode) return '24x7x365'
    return formatLocationHoursText(location, options)
  }

  checkLocationAvailability(location: Location, checkTime?: Date): boolean {
    if (this._isDebugMode) return true
    // Canonical holiday-aware gate (open now + 30-min pre-close cutoff).
    return isLocationAcceptingOrders(location, checkTime)
  }

  async getCurrentLocation(): Promise<UserLocation | null> {
    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported by this browser')
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              reject(new Error('Location permission denied'))
              break
            case error.POSITION_UNAVAILABLE:
              reject(new Error('Location information unavailable'))
              break
            case error.TIMEOUT:
              reject(new Error('Location request timed out'))
              break
            default:
              reject(new Error('An unknown error occurred while retrieving location'))
              break
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000 // Cache for 1 minute
        }
      )
    })
  }

  async setSelectedLocation(locationId: string): Promise<void> {
    try {
      localStorage.setItem(this.storageKey, locationId)
    } catch (error) {
      const logger = safeGetLogger()
      logger?.warn('Failed to save selected location', { error: String(error) })
      // Don't throw - this is not critical
    }
  }

  async getSelectedLocation(): Promise<string | null> {
    try {
      return localStorage.getItem(this.storageKey)
    } catch (error) {
      const logger = safeGetLogger()
      logger?.warn('Failed to load selected location', { error: String(error) })
      return null
    }
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180)
  }

}
